import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Brain,
  Book,
  Heart,
  Users,
  Clock,
  PlayCircle,
  ExternalLink,
  Calendar,
  Bell,
  Sparkles,
} from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { toast } from "sonner";
import { toggleBreakReminders, fetchTeamWellnessSettings, scheduleWellnessActivity } from "./services/teamWellnessService";

interface WellnessResource {
  id: string;
  title: string;
  description: string;
  category: "meditation" | "team-building" | "mental-health" | "coaching";
  duration?: string;
  link?: string;
  type: "article" | "video" | "exercise" | "tool";
}

const wellnessResources: WellnessResource[] = [
  // Meditation & Mindfulness
  {
    id: "1",
    title: "5-Minute Team Breathing Exercise",
    description: "Quick mindfulness exercise perfect for team meetings",
    category: "meditation",
    duration: "5 min",
    type: "exercise",
  },
  {
    id: "2",
    title: "Guided Stress Relief Meditation",
    description: "Help your team manage workplace stress effectively",
    category: "meditation",
    duration: "10 min",
    type: "video",
  },
  {
    id: "3",
    title: "Mindful Monday Team Ritual",
    description: "Start your week with intention and focus",
    category: "meditation",
    duration: "15 min",
    type: "exercise",
  },
  // Team Building
  {
    id: "4",
    title: "Virtual Team Building Activities",
    description: "20 engaging activities for remote teams",
    category: "team-building",
    type: "article",
  },
  {
    id: "5",
    title: "Building Psychological Safety",
    description: "Create an environment where team members feel safe to contribute",
    category: "team-building",
    type: "article",
  },
  {
    id: "6",
    title: "Team Retrospective Templates",
    description: "Effective formats for team reflection and improvement",
    category: "team-building",
    type: "tool",
  },
  // Mental Health
  {
    id: "7",
    title: "Recognizing Burnout Signs",
    description: "Early warning signs and prevention strategies",
    category: "mental-health",
    type: "article",
  },
  {
    id: "8",
    title: "Mental Health First Aid",
    description: "How to support team members in distress",
    category: "mental-health",
    type: "video",
  },
  {
    id: "9",
    title: "Work-Life Balance Strategies",
    description: "Practical tips for maintaining healthy boundaries",
    category: "mental-health",
    type: "article",
  },
  // Coaching
  {
    id: "10",
    title: "1-on-1 Meeting Templates",
    description: "Effective structures for meaningful conversations",
    category: "coaching",
    type: "tool",
  },
  {
    id: "11",
    title: "Giving Constructive Feedback",
    description: "Build trust while driving improvement",
    category: "coaching",
    type: "video",
  },
  {
    id: "12",
    title: "Team Goal Setting Workshop",
    description: "Align your team around shared objectives",
    category: "coaching",
    duration: "45 min",
    type: "exercise",
  },
];

interface TeamWellnessHubProps {
  teamId?: string;
}

export default function TeamWellnessHub({ teamId }: TeamWellnessHubProps) {
  const { user } = useUser();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [breakRemindersEnabled, setBreakRemindersEnabled] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Get team ID from prop or user context
  const effectiveTeamId = teamId || user?.teamId;

  // Load initial break reminder setting
  useEffect(() => {
    const loadSettings = async () => {
      if (!effectiveTeamId) return;
      
      const settings = await fetchTeamWellnessSettings(effectiveTeamId);
      if (settings) {
        setBreakRemindersEnabled(settings.break_reminders_enabled ?? false);
      }
    };
    loadSettings();
  }, [effectiveTeamId]);

  const filteredResources = selectedCategory === "all" 
    ? wellnessResources 
    : wellnessResources.filter(r => r.category === selectedCategory);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "meditation":
        return <Brain className="h-4 w-4" />;
      case "team-building":
        return <Users className="h-4 w-4" />;
      case "mental-health":
        return <Heart className="h-4 w-4" />;
      case "coaching":
        return <Sparkles className="h-4 w-4" />;
      default:
        return <Book className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "video":
        return <PlayCircle className="h-4 w-4" />;
      case "exercise":
        return <Clock className="h-4 w-4" />;
      case "tool":
        return <ExternalLink className="h-4 w-4" />;
      default:
        return <Book className="h-4 w-4" />;
    }
  };

  const handleBreakReminder = async () => {
    if (!effectiveTeamId) {
      toast.error("No team found. Please join or create a team first.");
      return;
    }

    setIsUpdating(true);
    const newValue = !breakRemindersEnabled;
    
    const success = await toggleBreakReminders(effectiveTeamId, newValue);
    
    if (success) {
      setBreakRemindersEnabled(newValue);
    }
    setIsUpdating(false);
  };

  const scheduleTeamMeditation = async () => {
    if (!effectiveTeamId) {
      toast.error("No team found. Please join or create a team first.");
      return;
    }

    const success = await scheduleWellnessActivity(effectiveTeamId, {
      type: "meditation",
      scheduledFor: new Date(Date.now() + 24 * 60 * 60 * 1000), // Tomorrow
      duration: 10,
      description: "Team guided meditation session",
    });

    if (!success) {
      // Error toast already shown by service
    }
  };

  const scheduleWellnessCheckIn = async () => {
    if (!effectiveTeamId) {
      toast.error("No team found. Please join or create a team first.");
      return;
    }

    const success = await scheduleWellnessActivity(effectiveTeamId, {
      type: "team-building",
      scheduledFor: new Date(Date.now() + 24 * 60 * 60 * 1000), // Tomorrow
      duration: 15,
      description: "Team wellness check-in - share how everyone is feeling",
    });

    if (!success) {
      // Error toast already shown by service
    }
  };

  return (
    <div className="space-y-6">
      {/* Break Reminders Card */}
      <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-lg">
                <Bell className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <CardTitle>Team Break Reminders</CardTitle>
                <CardDescription>
                  Help your team maintain healthy work habits
                </CardDescription>
              </div>
            </div>
            <Button
              variant={breakRemindersEnabled ? "default" : "outline"}
              size="sm"
              onClick={handleBreakReminder}
              disabled={isUpdating || !effectiveTeamId}
            >
              {isUpdating ? "Updating..." : breakRemindersEnabled ? "Enabled" : "Disabled"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <p className="text-sm font-medium">Quick Actions</p>
              <div className="space-y-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start"
                  onClick={scheduleTeamMeditation}
                  disabled={!effectiveTeamId}
                >
                  <Brain className="mr-2 h-4 w-4" />
                  Schedule Team Meditation
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start"
                  onClick={scheduleWellnessCheckIn}
                  disabled={!effectiveTeamId}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  Set Wellness Check-in
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Next Scheduled Breaks</p>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>• Team stretch break - 2:00 PM</p>
                <p>• Mindfulness moment - 4:00 PM</p>
                <p>• End of day reflection - 5:30 PM</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Wellness Resources */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
              <Book className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <CardTitle>Wellness Resource Library</CardTitle>
              <CardDescription>
                Curated resources to support your team's wellbeing
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="all" onClick={() => setSelectedCategory("all")}>
                All
              </TabsTrigger>
              <TabsTrigger value="meditation" onClick={() => setSelectedCategory("meditation")}>
                Meditation
              </TabsTrigger>
              <TabsTrigger value="team-building" onClick={() => setSelectedCategory("team-building")}>
                Team Building
              </TabsTrigger>
              <TabsTrigger value="mental-health" onClick={() => setSelectedCategory("mental-health")}>
                Mental Health
              </TabsTrigger>
              <TabsTrigger value="coaching" onClick={() => setSelectedCategory("coaching")}>
                Coaching
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="h-[400px] mt-4">
              <div className="space-y-3">
                {filteredResources.map((resource) => (
                  <Card key={resource.id} className="hover:shadow-md transition-shadow cursor-pointer">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            {getCategoryIcon(resource.category)}
                            <h4 className="font-medium">{resource.title}</h4>
                            {resource.duration && (
                              <Badge variant="secondary" className="text-xs">
                                {resource.duration}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {resource.description}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {getTypeIcon(resource.type)}
                              <span className="ml-1">{resource.type}</span>
                            </Badge>
                            <Button variant="ghost" size="sm">
                              Access Resource →
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </Tabs>
        </CardContent>
      </Card>

      {/* Team Coaching Recommendations */}
      <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
              <Sparkles className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <CardTitle>Personalized Coaching Recommendations</CardTitle>
              <CardDescription>
                AI-powered suggestions based on your team's patterns
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="p-4 bg-white dark:bg-card rounded-lg border">
              <h4 className="font-medium mb-2">Focus on Team Communication</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Based on recent check-ins, your team could benefit from improved communication channels.
              </p>
              <Button size="sm" variant="outline">
                View Communication Workshop
              </Button>
            </div>
            <div className="p-4 bg-white dark:bg-card rounded-lg border">
              <h4 className="font-medium mb-2">Energy Management Workshop</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Team energy levels have been fluctuating. Consider a workshop on sustainable pace.
              </p>
              <Button size="sm" variant="outline">
                Schedule Workshop
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}